import { useEffect, useState } from 'react'
import { useCallStore } from '../stores/callStore'
import { acceptCall, declineCall } from '../lib/data'
import { useT } from '../lib/i18n'
import { startRing, stopRing } from '../lib/sounds'
import { Avatar } from './Avatar'
import { IconButton } from './IconButton'
import { PhoneIcon, PhoneOffIcon, VideoIcon } from './Icons'
import { Spinner } from './Spinner'

/**
 * Full-screen ring. Driven by the `call.ring` WS frame while the app is open, and by push
 * notification navigation (`/call/:callId`) when it was closed (docs/CONTRACT.md §9).
 *
 * The one orchestrated piece of motion in the app: a lamp breathing in a dark room, which is what
 * the screen is announcing. Nothing else in the interface animates on arrival.
 *
 * Answer is brass, not green. Green/red is the conventional pair and the worst one to rely on — it
 * is exactly the pair that red-green colour vision deficiency collapses. Brass against red leaves
 * the two buttons separated by lightness and by hue for every common deficiency, and the icons
 * (handset / handset-down) and the words under them carry it for everyone else.
 */
export function IncomingCallRing() {
  const t = useT()
  const incoming = useCallStore((state) => state.incoming)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const incomingId = incoming?.call.id ?? null

  // The ring sounds for exactly as long as the ring exists: started when the store says this call
  // is incoming, stopped when it clears (answer, decline, `call.end`, or a sibling device's
  // `call.dismiss` — W7) or this overlay unmounts. It keeps ringing while the tab is hidden — that
  // is the point of a ring.
  useEffect(() => {
    if (!incomingId) return
    startRing()
    return () => stopRing()
  }, [incomingId])

  if (!incoming) return null

  const caller = incoming.caller
  const name = caller?.displayName ?? t('call.someone')
  const isVideo = incoming.call.kind === 'video'

  const accept = () => {
    setBusy(true)
    setError(null)
    void acceptCall(incoming.call.id)
      .catch(() => setError(t('call.couldNotJoin')))
      .finally(() => setBusy(false))
  }

  const decline = () => {
    setBusy(true)
    void declineCall(incoming.call.id).finally(() => setBusy(false))
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={
        isVideo
          ? t('call.incomingVideoCallFrom', { name })
          : t('call.incomingVoiceCallFrom', { name })
      }
      className="call-overlay ring-glow z-40 flex flex-col items-center justify-between"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <span className="relative flex items-center justify-center">
          <span aria-hidden="true" className="ring-halo absolute size-44 rounded-full bg-accent/20" />
          <span aria-hidden="true" className="absolute size-36 rounded-full ring-1 ring-accent/40" />
          <Avatar name={name} path={caller?.avatarUrl ?? null} size={112} className="relative" />
        </span>
        <div>
          <h2 dir="auto" className="text-display font-semibold text-ink">
            {name}
          </h2>
          <p className="mt-2 flex items-center justify-center gap-2 text-ui text-muted">
            {isVideo ? <VideoIcon size={16} /> : <PhoneIcon size={16} />}
            {isVideo ? t('call.incomingVideoCall') : t('call.incomingVoiceCall')}
          </p>
        </div>
        {error && (
          <p role="alert" className="text-ui text-danger">
            {error}
          </p>
        )}
      </div>

      <div className="safe-bottom flex items-center gap-16 pb-8">
        <div className="flex flex-col items-center gap-2.5">
          <IconButton label={t('call.declineCall')} variant="danger" size={68} disabled={busy} onClick={decline}>
            <PhoneOffIcon size={30} />
          </IconButton>
          <span aria-hidden="true" className="text-ui font-medium text-ink">
            {t('call.decline')}
          </span>
        </div>
        <div className="flex flex-col items-center gap-2.5">
          <IconButton label={t('call.answerCall')} variant="accent" size={68} disabled={busy} onClick={accept}>
            {busy ? <Spinner size={26} label={t('call.joining')} /> : <PhoneIcon size={30} />}
          </IconButton>
          <span aria-hidden="true" className="text-ui font-medium text-ink">
            {t('call.answer')}
          </span>
        </div>
      </div>
    </div>
  )
}
